import Auth from '@aws-amplify/auth';
import Bugsnag from '@bugsnag/js';
import config from '../config';
import i18n, { i18nReady } from '../i18n';

export function normalizeLanguage(value) {
  if (typeof value !== 'string') return null;
  const code = value.toLowerCase().split(/[-_]/)[0];
  return ['en', 'vi', 'id'].indexOf(code) >= 0 ? code : null;
}

let initialization = 0;
let changes = Promise.resolve();
export function invalidateChartLanguage() { initialization += 1; }

function applyLanguage(language, started) {
  const pending = changes.then(async () => {
    // Initial browser-language loading must finish before applying chart context.
    // Otherwise its late completion can overwrite an explicit chart language.
    try {
      await i18nReady;
    } catch (error) {
      Bugsnag.notify(error);
      // A failed default-language initialization must not block chart context.
    }
    if (started === initialization) return i18n.changeLanguage(language);
    return undefined;
  });
  changes = pending.catch(error => { Bugsnag.notify(error); });
  return changes;
}

export async function initializeChartLanguage(auth, search = window.location.search) {
  const started = ++initialization;
  let params = new URLSearchParams(search);
  const redirect = params.get('redirect');
  if (!params.has('lang') && !params.has('language') && redirect) {
    try { params = new URL(atob(redirect), window.location.origin).searchParams; }
    catch (error) { Bugsnag.notify(error); }
  }
  // CPI-1284's Storey Plan rewrites language=EN|VN on each chart navigation.
  // It wins over any older lang parameter retained in the cached link.
  const storeyLanguage = (params.get('language') || '').trim().toLowerCase();
  const carried = params.has('language')
    ? (storeyLanguage === 'vn' || /^vi(?:[-_]|$)/.test(storeyLanguage) ? 'vi' : 'en')
    : normalizeLanguage(params.get('lang'));
  if (carried) {
    await applyLanguage(carried, started);
    return;
  }
  const fallback = normalizeLanguage(auth && (auth.currentLanguageCode || auth.language));
  if (fallback) await applyLanguage(fallback, started);
  if (config.app.shareMode) return;
  try {
    const session = await Auth.currentSession();
    const response = await fetch(`${config.gateways.API_URL}/authenticated/language/`, {
      credentials: 'include', cache: 'no-store',
      headers: { 'X-Aws-Id-Token': session.getIdToken().getJwtToken() },
    });
    if (!response.ok) throw new Error(`Chart language read failed (${response.status})`);
    const data = await response.json();
    const language = normalizeLanguage(data.currentLanguageCode);
    if (language && started === initialization) await applyLanguage(language, started);
  } catch (error) { Bugsnag.notify(error); }
}
